import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridColumnHeaderParams } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";

import { LeadData } from "../../types/leads.types";

interface Props {
  rows: LeadData[];
  columns: GridColDef<LeadData>[];
  loading: boolean;
  pageSize: number;
  currentPage: number;
  onPageChange: (newPage: number) => void;
  onRowClick?: (rowData: LeadData) => void;
}

const STEP = 200;

const LeadsImportedGrid = ({ rows, columns, loading, onRowClick }: Props) => {
  const [cols, setCols] = useState<GridColDef<LeadData>[]>([]);
  const [fileNameToggled, setFileNameToggled] = useState(false);
  const [fileNameBaseWidth, setFileNameBaseWidth] = useState<number | null>(null);

  useEffect(() => {
    const withHeaderControls = columns.map((col) => {
      if (col.field !== "fileName") return col;

      const base = col.width ?? 160;
      if (fileNameBaseWidth === null) setFileNameBaseWidth(base);

      const origRenderHeader = col.renderHeader;
      const enhanced: GridColDef<LeadData> = {
        ...col,
        width: fileNameToggled ? base + STEP : base,
        renderHeader: (params: GridColumnHeaderParams<LeadData>) => {
          const headerContent = origRenderHeader
            ? origRenderHeader(params)
            : <span>{col.headerName ?? col.field}</span>;

          const toggleWidth = () => {
            setFileNameToggled((prev) => !prev);
            setCols((prev) =>
              prev.map((c) =>
                c.field === "fileName"
                  ? {
                      ...c,
                      width: (fileNameBaseWidth ?? base) + (fileNameToggled ? 0 : STEP),
                    }
                  : c
              )
            );
          };

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
              <Box sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {headerContent}
              </Box>
              <Tooltip title={fileNameToggled ? "Collapse" : "Expand"}>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleWidth();
                  }}
                  aria-label={fileNameToggled ? "Collapse" : "Expand"}
                  sx={{ ml: "auto" }}
                >
                  {fileNameToggled ? (
                    <ChevronLeftIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      };

      return enhanced;
    });

    setCols(withHeaderControls);
  }, [columns, fileNameToggled, fileNameBaseWidth]);

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        overflowY: "visible",
        borderRadius: "16px 16px 0 0",
        border: "1px solid #e0e0e0",
        position: "relative",
      }}
    >
      <DataGrid
        loading={loading}
        rows={rows}
        columns={cols}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        disableVirtualization
        onRowClick={params => {
          if (onRowClick) {
            onRowClick(params.row);
          }
        }}
        sx={{
          border: "1px solid #e0e0e0",
          minWidth: "max-content",
          "& .MuiDataGrid-main": {
            overflowX: "visible",
            overflowY: "visible",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "visible !important",
            overflowY: "visible !important",
            minHeight: rows.length === 0 ? "300px" : "auto",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#E77825",
            color: "#FFF",
            fontWeight: 800,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "16px",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        slotProps={{
          noRowsOverlay: {
            sx: {
              height: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          },
        }}
      />
    </Box>
  );
};

export default LeadsImportedGrid;
